import type { ManualContext } from '../intelligence/contextLab/manualContexts'
import type { Mark } from '../contextsV2/store'
import type { Statement } from '../contextsV2/statements'

/**
 * Contexts in the person's export (pre-launch C3; privacy policy: "the Context you create, which notes belong to it, your ticks, choices
 * and hidden items"). What is exported is what the person made or did: each Context's name, purpose and notes, and every act of theirs in it.
 * The organized view is not exported: it is made from the notes and rebuilt on demand. A deleted Context is not exported.
 */
export interface ExportedContext {
  id: string
  title: string
  purpose?: string
  createdAt: string
  notes: { id: string; title: string; addedAt: string }[]
  /** Every act of the person's in this Context, as stored: ticks, chosen options, dates, hidden items and notes removed from it. */
  actions: { kind: string; value: unknown; item: string; at: string }[]
}
export interface ContextsExport { version: 1; contexts: ExportedContext[]; watched: { key: string; watched: boolean; at: string }[] }

const iso = (ms: number) => new Date(ms).toISOString()

export function buildContextsExport(input: {
  contexts: readonly ManualContext[]
  statements: readonly Statement[]
  marks: readonly Mark[]
  /** Titles by note id, for the notes that exist. */
  noteTitles: ReadonlyMap<string, string>
}): ContextsExport {
  const contexts = input.contexts.filter(context => !context.archivedAt).sort((a, b) => a.createdAt - b.createdAt).map((context): ExportedContext => ({
    id: context.id,
    title: context.title,
    ...(context.purpose ? { purpose: context.purpose } : {}),
    createdAt: iso(context.createdAt),
    notes: context.links.map(link => ({ id: link.noteId, title: input.noteTitles.get(link.noteId) ?? '(note no longer available)', addedAt: iso(link.addedAt) })),
    actions: input.statements.filter(statement => statement.contextId === context.id)
      .sort((a, b) => a.at.localeCompare(b.at))
      .map(statement => ({ kind: statement.kind, value: statement.value, item: statement.anchor.text, at: statement.at })),
  }))
  return { version: 1, contexts, watched: input.marks.map(mark => ({ key: mark.key, watched: mark.watched, at: mark.at })) }
}

const label: Record<string, string> = { done: 'Ticked', chosen: 'Chosen', hidden_item: 'Hidden', hidden: 'Removed from this Context', date: 'Date' }

function actionLine(action: ExportedContext['actions'][number]): string | null {
  if (action.kind === 'date') {
    const value = action.value as { to?: string | null; kept?: string }
    const what = value.to === null ? 'removed' : value.to ? `changed to ${value.to}` : value.kept ? `kept (${value.kept})` : null
    return what ? `- Date: ${action.item} ${what}` : null
  }
  if (action.value !== true) return null
  return `- ${label[action.kind] ?? action.kind}: ${action.item}`
}

export function contextsAsMarkdown(exported: ContextsExport): string {
  if (!exported.contexts.length) return ''
  return exported.contexts.map(context => {
    const lines = [`# Context: ${context.title}`, '']
    if (context.purpose) lines.push(context.purpose, '')
    lines.push('Notes:', ...context.notes.map(note => `- ${note.title}`))
    const actions = context.actions.map(actionLine).filter((line): line is string => line !== null)
    if (actions.length) lines.push('', 'Your choices in this Context:', ...actions)
    return lines.join('\n') + '\n'
  }).join('\n---\n\n')
}
